/* eslint-disable multiline-ternary */
/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles, { filterTableWidth } from './styles';
import { Box, Checkbox, CircularProgress, Dialog, Divider, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { ArrowDropUp, CancelPresentationRounded, ChevronLeft, ChevronRight, Circle, Close, FilterAltRounded, LinearScaleOutlined, RestoreOutlined, ShortcutOutlined, Square } from '@mui/icons-material';
import CanvasJSReact from '../../canvasjs/canvasjs.react';
import Datagrid from '../../Datagrid';
import columns from './table_config';
import { useDispatch, useSelector } from 'react-redux';
import { HYDRAULICS_CHART_COLORS } from '../../../../shared/config/hydraulics_constants';
import { getOptimalInterval } from '../../../../utils/getOptimalInterval';
import { FILTER_STYLE_PARAMETRIC_ANALYSIS, HYDRAULICS_RUN_TYPES, MAIN_DEPTH_POINTS, PARAMETER_ANALYSIS_FILTER_PARAMS, PARAMETRIC_ANALYSIS_FILTER_LABEL, REPORT_INPUT_LIST, TOOLTIPS } from '../../../../utils/constants';
import { COLORS } from '../../../../shared/config/colors';
import { listParametricAnalysisResults } from '../../../../store/actions/hydraulicsParameterAnalysis';
import { downloadDataReport, downloadParametricPDF, listHydraulicsDetails } from '../../../../store/actions/hydraulics';
import ChartsAxisPopover from '../../ChartsAxisPopover';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';
import { getMinMaxOfKey } from '../../../../utils/objectHelpers';
import RunChartDialog from '../RunDetailsChartDialog';
import ReportInputsModal from '../ReportInputsModal';
import HydraulicsDetailsModal from '../HydraulicsRunModalDialog';
import { listWindow } from '../../../../store/actions/wellsWindow';
import ReportsDownloadSelector from '../ReportsDownloadSelector';
import VersionText from '../VersionText';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const ParametricAnalysisChart = ({
  onCloseClick,
  isOpen,
  rowData,
  wellDetails,
  hydraulicWellsDetails
}) => {
  const classes = styles();
  const [tableRow, setTableRows] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const hydraulics = useSelector((state) => state.hydraulics);
  const { parametricAnalysisChartData } = useSelector((state) => state.hydraulicsParameterAnalysis);
  const { downloadParametricAnalysisPdf, chartXAxis, chartYAxis } = useSelector((state) => state.hydraulics);
  const dispatch = useDispatch();
  const [plotFor, setPlotFor] = useState('ECD');
  const window = useSelector((state) => state.wellsWindow);
  const [chartData, setChartData] = useState([]);
  const [showRunChart, setShowRunChart] = useState({
    open: false,
    data: null
  });
  const containerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const [showFilter, setShowFilter] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [hiddenChartData, setHiddenChartData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [minMax_X, setMinMax_X] = useState({});
  const [minMax_Y, setMinMax_Y] = useState({});
  const [defaultXAxis, setDefaultXAxis] = useState({});
  const [defaultYAxis, setDefaultYAxis] = useState({});
  const [customColumn, setCustomColumn] = useState({
    header: null,
    field: null,
    show: true
  });
  const [showReportInput, setShowReportInputs] = useState(false);
  const [tableList, setTableList] = useState(REPORT_INPUT_LIST.map(item => item.key));
  const [reportPreview, setReportPreview] = useState(false);
  const [showHydraulicsParameterDialog, setShowHydraulicsParameterDialog] = useState({
    open: false,
    data: {},
    dialogParameter: ''
  });

  useEffect(() => {
    if (loader || hydraulics?.loading) {
      document.body.style.cursor = 'wait';
    } else {
      document.body.style.cursor = 'default';
    }
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [loader, hydraulics]);

  const handleScroll = () => {
    if (containerRef.current) {
      scrollPositionRef.current = containerRef.current.scrollTop;
    }
  };

  const [chartConfig, setChartConfig] = useState([]);

  useEffect(() => {
    setChartConfig([
      {
        name: 'PP',
        key: 'pp',
        color: HYDRAULICS_CHART_COLORS.PORE_PRESSURE,
        hide: false,
        tooltip: `MD: {y} ${window?.units?.md?.label} <br/> PP: {x} ${window?.units?.pp?.label}`
      },
      {
        name: 'FP',
        key: 'fp',
        color: HYDRAULICS_CHART_COLORS.FRACTURE_PRESSURE,
        hide: false,
        tooltip: `MD: {y} ${window?.units?.md?.label} <br/> FP: {x} ${window?.units?.fp?.label}`
      },
    ]);
  }, [window.loading]);

  useEffect(() => {
    const body = {
      runId: rowData?._id,
      wellsInfoId: rowData?.wellsInfoId
    };
    if (rowData?.isParametricResults) {
      dispatch(listParametricAnalysisResults(body));
    }
  }, [rowData]);

  useEffect(() => {
    if (!window?.data.length) {
      dispatch(listWindow(rowData?.wellsInfoId));
    }
  }, []);

  useEffect(() => {
    if (!parametricAnalysisChartData?.inputs?.length) return;

    const inputs = parametricAnalysisChartData.inputs;
    const maxLength = Math.max(...inputs.map(({ values }) => values.length));
    const transformed = Array.from({ length: maxLength }, (_, i) => {
      const getFieldName = (index) => {
        const names = ['First', 'Second', 'Third', 'Fourth'];
        return names[index] ? `${names[index]} Value` : `Value#${index + 1}`;
      };

      const row = {
        _id: Math.random().toString(36).substring(2, 10),
        label: `Value#${i + 1}`,
        labelName: getFieldName(i),
        isChecked: {}
      };
      inputs.forEach(({ parameter, values }) => {
        row[parameter] = values[i];
        row.isChecked[parameter] = true;
      });
      return row;
    });
    setFilterData(transformed);
  }, [parametricAnalysisChartData]);

  useEffect(() => {
    setShowFilter(!parametricAnalysisChartData.loading && !!parametricAnalysisChartData.results);
    setShowTable(!parametricAnalysisChartData.loading && !!parametricAnalysisChartData.results);
  }, [parametricAnalysisChartData.loading, parametricAnalysisChartData.results]);

  useEffect(() => {
    if (!parametricAnalysisChartData?.results?.length && !filterData?.length) return;
    const targetData = parametricAnalysisChartData?.run_details_copy?.target_data;
    const anchorPoint = targetData?.anchor_point;
    const isTargetDataEmpty = !targetData || Object.keys(targetData).length === 0 || targetData?.anchor_point === 'None';
    const isExcluded = MAIN_DEPTH_POINTS.includes(anchorPoint);
    if (isTargetDataEmpty) {
      setCustomColumn({ header: null, show: true, field: null });
    } else {
      const headerName = anchorPoint.replace(/_/g, ' ').toUpperCase();
      const fieldName = anchorPoint.replace(/ /g, '_')?.toLowerCase();
      setCustomColumn(
        isExcluded
          ? { header: null, show: true, field: null }
          : { header: headerName, show: false, field: `${fieldName}` }
      );
    }
    const plotKey = plotFor === 'ECD' ? 'ecd_with_sbp' : 'esd_with_sbp';
    const plotName = plotFor;
    const units = parametricAnalysisChartData.units?.depth_points;

    const getStyle = (value, key) => {
      const values = PARAMETRIC_ANALYSIS_FILTER_LABEL.map(label => filterData.find(item => item.label === label)?.[key] ?? 0);
      return PARAMETRIC_ANALYSIS_FILTER_LABEL[values.indexOf(value)];
    };

    const processedData = parametricAnalysisChartData.results.map(({ depth_points = [], depths_summary = {}, inputs = {}, summary = {} }, index) => {
      const styles = parametricAnalysisChartData.inputs.reduce((acc, { parameter }) => {
        acc[parameter] = getStyle(inputs[parameter], parameter);
        return acc;
      }, {});
      return {
        summary,
        depth_points,
        depths_summary,
        inputs,
        hide: false,
        name: plotName,
        key: plotKey,
        index,
        runNumber: index + 1,
        type: 'line',
        color: FILTER_STYLE_PARAMETRIC_ANALYSIS[styles[parametricAnalysisChartData?.inputs[0]?.parameter]]?.color || 'grey',
        markerType: FILTER_STYLE_PARAMETRIC_ANALYSIS[styles[parametricAnalysisChartData?.inputs[1]?.parameter]]?.markerType || '',
        lineDashStyle: FILTER_STYLE_PARAMETRIC_ANALYSIS[styles[parametricAnalysisChartData?.inputs[2]?.parameter]]?.lineDashStyle || 'solid',
        lineDashType: FILTER_STYLE_PARAMETRIC_ANALYSIS[styles[parametricAnalysisChartData?.inputs[2]?.parameter]]?.lineDashType || 'solid',
        lineThickness: 1,
        toolTipContent: `Run ${index + 1} <br/> MD: {y} ${units?.md?.label} <br/> ${plotName}: {${plotKey}} ${units?.esd?.label} <br/>`,
        showInLegend: false,
        dataPoints: depth_points.map(({ md, ...item }) => ({
          x: item[plotKey],
          y: md,
          ...item,
        })),
      };
    });

    const records = processedData.map((record, index) => {
      const { depths_summary = {}, inputs = {}, summary = {} } = record;
      const casingShoe = depths_summary.casing_shoe?.[plotKey] ?? null;
      const landingPoint = depths_summary.landing_point?.[plotKey] ?? null;
      const bottomHole = depths_summary.hole_depth?.[plotKey] ?? null;
      const connectionSbp = summary.connection_sbp ?? null;
      const baseRecord = {
        ...record,
        index,
        hide: true,
        runNumber: index + 1,
        casing_shoe: casingShoe,
        landing_point: landingPoint,
        bottom_hole: bottomHole,
        connection_sbp: connectionSbp,
        _id: Math.random().toString(36).substring(2, 10),
        rop: plotFor === 'ESD' ? `(${inputs?.rop}>>0)` : inputs.rop ?? 0.0,
        rpm: plotFor === 'ESD' ? `(${inputs?.rpm}>>0)` : inputs.rpm ?? 0.0,
        flow_in: plotFor === 'ESD' ? `(${inputs?.flow_in}>>0)` : inputs.flow_in ?? null,
        dynamic_sbp: summary.target_sbp ?? null,
        mw: inputs.mw ?? null,
        hole_depth: inputs.hole_depth ?? null,
      };

      if (!isTargetDataEmpty && !isExcluded) {
        const fieldName = anchorPoint.replace(/ /g, '_')?.toLowerCase();
        const anchorValue = depths_summary?.[fieldName]?.[plotKey] ?? null;
        baseRecord[fieldName] = anchorValue;
      }

      return baseRecord;
    });

    const isAllFalseForAnyParam = PARAMETER_ANALYSIS_FILTER_PARAMS.some(param => {
      return filterData.every(item => item.isChecked[param] === false);
    });

    if (isAllFalseForAnyParam) {
      setChartData([]);
      setTableRows([]);
      return;
    }

    const conditions = filterData.reduce((acc, { isChecked, ...criteria }) => {
      Object.entries(isChecked).forEach(([key, checked]) => {
        if (checked) {
          acc[key] = acc[key] || new Set();
          acc[key].add(criteria[key]);
        }
      });
      return acc;
    }, {});

    const isMatch = ({ inputs }) => Object.entries(conditions).every(([key, values]) => values.has(inputs[key]));
    const processedChartData = processedData.filter(isMatch);
    const processDepthPoints = processedChartData.map((item) => {
      return item.dataPoints;
    }).flatMap(item => item);
    const axisY = getMinMaxOfKey(processDepthPoints, 'y');
    setMinMax_Y({
      min: axisY.min,
      max: axisY.max,
    });
    setChartData(processedChartData);
    const x_axis_viewPort = generateViewportMinMax(processedChartData);
    if (x_axis_viewPort) {
      setMinMax_X(x_axis_viewPort);
    }
    setHiddenChartData(processedChartData);
    setTableRows(records.filter(isMatch));
  }, [parametricAnalysisChartData, plotFor, filterData]);

  const handleResetAll = () => {
    setFilterData((prevData) =>
      prevData.map((item) => ({
        ...item,
        isChecked: Object.fromEntries(
          Object.keys(item.isChecked).map((key) => [key, true])
        ),
      }))
    );
  };

  const columns_table = [
    ...(parametricAnalysisChartData?.inputs || []).map((col, index) => ({
      field: col.parameter,
      headerName: col.parameter === 'flow_in'
        ? 'Flow Rate'
        : col.parameter === 'hole_depth'
          ? 'Hole Depth'
          : col.parameter.toUpperCase(),
      renderHeader: () => (
        <Box>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
            {col.parameter === 'flow_in'
              ? 'Flow Rate'
              : col.parameter === 'hole_depth'
                ? 'Hole Depth'
                : col.parameter.toUpperCase()}
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
            ({parametricAnalysisChartData?.units?.inputs?.[col.parameter]?.label || ''})
          </Typography>
        </Box>
      ),
      width: 190,
      headerAlign: 'center',
      headerClassName: 'table-header',
      renderCell: (params) => {
        const row = params.row;
        const isChecked = row.isChecked[col.parameter];

        return (
          <Box
            className={classes.checkBoxCell}
            sx={{
              background: isChecked && params.value !== undefined ? COLORS.WHITE : 'default',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '50%', justifyContent: 'center' }}>
              {params.value !== undefined && <Checkbox
                size="small"
                checked={row.isChecked[col.parameter]}
                onChange={() => handleCheckboxChange(row._id, col.parameter)}
              />}
              <Box
                className={classes.checkBoxValue}
              >
                {params?.value?.toFixed(parametricAnalysisChartData?.units?.inputs?.[col.parameter]?.decimals)}
              </Box>
            </Box>

            <Box sx={{ flexShrink: 0, width: '50%', display: 'flex', justifyContent: 'center' }}>
              {index === 1 && params.value !== undefined && (
                <IconButton
                  className={classes.filterTableMarker}
                >
                  {row?.label === 'Value#3' && <ArrowDropUp sx={{ fontSize: 50, color: COLORS.GRAY }} />}
                  {row?.label === 'Value#1' && <Circle sx={{ fontSize: 15, color: COLORS.GRAY }} />}
                  {row?.label === 'Value#2' && <Square sx={{ fontSize: 15, color: COLORS.GRAY }} />}
                  {row?.label === 'Value#4' && <Close sx={{ fontSize: 25, color: COLORS.GRAY }} />}
                </IconButton>
              )}

              {index === 2 && params.value !== undefined && (
                <Box
                  className={classes.lineDashType}
                  sx={
                    row.label === 'Value#4'
                      ? {
                          width: '100px',
                          borderTop: '2px solid',
                          borderImage: `repeating-linear-gradient(
                            to right,
                            grey 0,
                            grey 2px,
                            transparent 2px,
                            transparent 6px,
                            grey 6px,
                            grey 14px,
                            transparent 14px,
                            transparent 18px
                          ) 1`,
                        }
                      : {
                          border: `2px ${FILTER_STYLE_PARAMETRIC_ANALYSIS[row.label]?.lineDashStyle}`,
                        }
                  }
                />
              )}

              {index === 0 && params?.value !== undefined && (
                <Box
                  className={classes.lineColorFlow_in}
                  sx={{
                    border: `2px solid ${FILTER_STYLE_PARAMETRIC_ANALYSIS[row.label]?.color}`,
                  }}
                />
              )}
            </Box>
          </Box>
        );
      },
    })),
    {
      field: 'reset all',
      headerName: 'Reset',
      renderHeader: () => (
        <Box>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
            Reset
          </Typography>
        </Box>
      ),
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (params.row.label === 'Value#1') {
          return (
            <IconButton onClick={() => handleResetAll()}>
              <RestoreOutlined />
            </IconButton>
          );
        }
        return null;
      },
    },
  ];

  const handleCheckboxChange = (rowIndex, field) => {
    setFilterData(prevData =>
      prevData.map((row) =>
        row?._id === rowIndex
          ? { ...row, isChecked: { ...row.isChecked, [field]: !row.isChecked[field] } }
          : row
      )
    );
  };

  useEffect(() => {
    if (minMax_X) setDefaultXAxis(minMax_X);
    if (minMax_Y) setDefaultYAxis(minMax_Y);
  }, [minMax_X, minMax_Y]);

  const generateViewportMinMax = (chartData) => {
    const extractAllDepthPointsArrays = (data) => {
      return data.flatMap(item => item.depth_points);
    };

    const allDepthPoints = extractAllDepthPointsArrays(chartData);
    const minMaxData = [...allDepthPoints, ...window.data];
    const keysToCheck = ['esd_with_sbp', 'ecd_with_sbp', 'pp', 'fp'];

    const allValues = [];

    // Collect all values from the specified keys
    keysToCheck.forEach(key => {
      minMaxData.forEach(obj => {
        if (key in obj) {
          allValues.push(obj[key]);
        }
      });
    });

    // Calculate the overall lowest and highest values
    const overallLowest = Math.min(...allValues);
    const overallHighest = Math.max(...allValues);

    const results = {
      lowest: overallLowest,
      highest: overallHighest,
    };

    return results;
  };

  const generateWindowDataPoints = (config) => {
    const extractAllDepthPointsArrays = (data) => {
      return data.flatMap(item => item.depth_points);
    };

    const allDepthPoints = extractAllDepthPointsArrays(chartData);
    const dataPoints = [];

    if (window?.data.length === 1) {
      const singleDataPoint = window?.data[0];
      dataPoints.push(
        { x: singleDataPoint[config.key], y: 0, markerType: 'none' },
        { x: singleDataPoint[config.key], y: singleDataPoint.md },
        { x: singleDataPoint[config.key], y: allDepthPoints.at(-1).md, markerType: 'none' }
      );
    } else if (window?.data.length > 1) {
      const firstPoint = window?.data[0];
      const lastPoint = window?.data.at(-1);

      if (firstPoint.md > 0) {
        dataPoints.push({ x: firstPoint[config.key], y: 0, markerType: 'none' });
      }

      window?.data.forEach(item => {
        dataPoints.push({ x: item[config.key], y: item.md });
      });

      dataPoints.push({ x: lastPoint[config.key], y: allDepthPoints.at(-1).md, markerType: 'none' });
    }

    const formattedData = dataPoints.map(item => {
      const formattedItem = {
        x: parseFloat(Number(item.x).toFixed(window?.units?.md?.decimals)),
        y: parseFloat(Number(item.y).toFixed(window?.units?.pp?.decimals)),
      };
      if (item.markerType) {
        formattedItem.markerType = item.markerType;
      }
      return formattedItem;
    });
    return formattedData;
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [chartData]);

  const CustomLegend = () => {
    const updateHide = (key, hideValue) => {
      handleScroll();
      setChartConfig(prevItems =>
        prevItems.map(item =>
          item.key === key ? { ...item, hide: hideValue } : item
        )
      );
    };

    return parametricAnalysisChartData.loading
      ? null
      : (
        <Grid
          container
          ref={containerRef}
          onScroll={handleScroll}
          height={'20px'}
        >
          {chartConfig.map((item) => (
            <Box
              key={item.name}
              className={classes.legendItem}
              sx={{ opacity: item.hide ? 0.4 : 1 }}
              onClick={() => {
                updateHide(item.key, !item.hide);
              }}
            >
              <LinearScaleOutlined fontSize="large" sx={{ color: item.color, mr: 1 }} />
              {item.name}
            </Box>
          ))}
        </Grid>
        );
  };

  const getValidValue = (val, fallback) =>
    val !== undefined && val !== null && val !== '' ? val : fallback;

  const xMin = getValidValue(chartXAxis?.min, minMax_X?.lowest - 0.1);
  const xMax = getValidValue(chartXAxis?.max, minMax_X?.highest + 0.1);
  const yMin = getValidValue(chartYAxis?.min, null);
  const yMax = getValidValue(chartYAxis?.max, null);

  const options = useMemo(() => ({
    theme: 'light2',
    animationEnabled: true,
    zoomEnabled: true,
    exportEnabled: true,
    zoomType: 'y',
    toolbar: {
      itemBackgroundColor: '#d3d3d3',
      itemBackgroundColorOnHover: '#3e3e3e',
      buttonBorderColor: '#3e3e3e',
    },
    backgroundColor: '#FFF',
    axisX: {
      viewportMinimum: xMin,
      viewportMaximum: xMax,
      interval: getOptimalInterval(xMin, xMax, 10),
      valueFormatString: '0.0',
      labelFontSize: 16,
      gridThickness: 1,
      title: `EMW (${parametricAnalysisChartData?.units?.depth_points?.esd?.label})`,
      titleFontSize: 22,
    },
    axisY: {
      viewportMinimum: yMin,
      viewportMaximum: yMax,
      title: `MD (${parametricAnalysisChartData?.units?.depth_points?.md?.label})`,
      titleFontSize: 22,
      labelFontSize: 16,
      includeZero: false,
      valueFormatString: '0',
      reversed: true,
      labelPlacement: 'outside',
      gridThickness: 1,
    },
    height: 800,
    legend: {
      fontSize: 20,
    },
    data: [
      ...chartConfig.map((config) => {
        if (chartData.length > 0) {
          if (config.hide) return {};
          if (config.key === 'pp' || config.key === 'fp') {
            return {
              type: 'line',
              name: config.name,
              showInLegend: false,
              toolTipContent: config.tooltip,
              dataPoints: generateWindowDataPoints(config),
              color: config.color,
            };
          }
        }
        return {};
      }),
      ...chartData,
    ],
  }), [chartConfig, chartData, chartXAxis, chartYAxis]);

  const handleChartAxisReset = (target) => {
    if (target === 'x' || target === 'resetAll') {
      dispatch({
        type: REDUX_ACTIONS.SET_HYDRAULICS_ECD_CHARTS_XAXIS,
        payload: { min: '', max: '' },
      });
    }

    if (target === 'y' || target === 'resetAll') {
      dispatch({
        type: REDUX_ACTIONS.SET_HYDRAULICS_ECD_CHARTS_YAXIS,
        payload: { min: '', max: '' },
      });
    }
  };

  const renderRunCell = (params) => {
    const { row } = params;
    const borderStyle = row?.lineDashStyle || 'solid';
    const lineThickness = row?.lineThickness || 2;

    const iconButtonSx =
      borderStyle !== 'dashDot'
        ? {
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              borderTop: `${lineThickness}px ${borderStyle} ${row?.color}`,
              transform: 'translateY(-50%)',
            },
          }
        : {
            width: '50px',
            height: '60px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: '2px',
              borderTop: '2px solid',
              borderImage: `repeating-linear-gradient(
                to right,
                ${row?.color} 0,
                ${row?.color} 2px,
                transparent 2px,
                transparent 6px,
                ${row?.color} 6px,
                ${row?.color} 14px,
                transparent 14px,
                transparent 18px
              ) 1`,
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            },
          };

    return (
      <Box style={{ display: 'flex', alignItems: 'center' }}>
        <Typography style={{ fontWeight: 800, fontSize: '1rem', color: row?.color }}>
          {row?.runNumber}
        </Typography>
        <IconButton
          className={classes.runDetailsyTableMarker}
          sx={iconButtonSx}
        >
          {row?.markerType === 'triangle' && <ArrowDropUp sx={{ fontSize: 50, color: row?.color }} />}
          {row?.markerType === 'circle' && <Circle sx={{ fontSize: 15, color: row?.color }} />}
          {row?.markerType === 'square' && <Square sx={{ fontSize: 15, color: row?.color }} />}
          {row?.markerType === 'cross' && <Close sx={{ fontSize: 25, color: row?.color }} />}
        </IconButton>
      </Box>
    );
  };

  useEffect(() => {
    const filteredData = hiddenChartData.filter(item => {
      const row = tableRow.find(row => row.index === item.index);
      return row?.hide;
    });
    const isAllFalseForAnyParam = PARAMETER_ANALYSIS_FILTER_PARAMS.some(param => {
      return filterData.every(item => item.isChecked[param] === false);
    });
    if (isAllFalseForAnyParam) {
      setChartData([]);
      return;
    }
    setChartData(filteredData);
  }, [tableRow]);

  const updateHideRunTable = (key, hideValue) => {
    setTableRows(prevItems =>
      prevItems.map(item =>
        item.index === key ? { ...item, hide: !hideValue } : item
      )
    );
  };

  const renderRunHideCell = (params) => {
    return (
      <Box
        className={classes.checkBoxCell}
        sx={{
        }}
      >
        <Checkbox
          size="small"
          checked={params?.row?.hide}
          onClick={() => {
            updateHideRunTable(params?.row?.index, params?.row?.hide);
          }}
        />
      </Box>
    );
  };

  const handleDownloadPDF = () => {
    const body = {
      inputs: tableList,
      jobId: rowData?.jobId,
      wellsInfoId: rowData?.wellsInfoId,
      runId: rowData?._id,
      wellName: wellDetails?.Well,
      rigName: wellDetails?.Rig,
      runName: rowData?.name,
      type: plotFor,
      xAxis: {
        min: parseFloat(((xMin ?? (minMax_X.lowest - 0.1))).toFixed(2)),
        max: parseFloat(((xMax ?? (minMax_X.highest + 0.1))).toFixed(2)),
      },
      yAxis: {
        min: parseFloat(((yMin ?? Number(minMax_Y.min))).toFixed(2)),
        max: parseFloat(((yMax ?? Number(minMax_Y.max))).toFixed(2)),
      },
    };
    handleResetAll();
    setLoader(true);
    dispatch(downloadParametricPDF(body, reportPreview, () => {
      setLoader(false);
    }));
  };

  const handleDataReportDownload = () => {
    setLoader(true);
    const body = {
      jobId: rowData?.jobId,
      wellsInfoId: rowData?.wellsInfoId,
      runId: rowData?._id,
      wellName: wellDetails?.Well,
      rigName: wellDetails?.Rig,
      runName: rowData?.name,
      type: HYDRAULICS_RUN_TYPES?.PARAMETRIC_ANALYSIS?.typeKey,
    };
    dispatch(downloadDataReport(body, () => {
      setLoader(false);
    }));
  };

  return (
    <React.Fragment>
      <Dialog
        open={isOpen}
        onClose={() => {
          onCloseClick();
          handleChartAxisReset('resetAll');
        }}
        maxWidth={'lg'}
        fullWidth={true}
        style={{
          marginRight: '20px'
        }}
        PaperProps={{
          sx: {
            overflow: 'visible'
          },
        }}
        className={classes.dialogModal}
      >
        {parametricAnalysisChartData?.loading || hydraulics?.loading
          ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ height: '700px' }}
            >
              <CircularProgress />
            </Box>
            )
          : (
            <>
        <Box className={classes.dialogTitle}>
          <Typography sx={{ fontWeight: 'bold', fontSize: '18px' }}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                {`${wellDetails?.Rig} | ${wellDetails?.Well} | ${rowData?.name}`}
              </Grid>
              <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                {rowData?.isParametricResults ? (
                  (
                    <ReportsDownloadSelector
                      loading={loader || downloadParametricAnalysisPdf === 'loading' || parametricAnalysisChartData.loading || hydraulics?.loading}
                      onDataDownload={handleDataReportDownload}
                      onPreviewOpen={(previewItem) => {
                        setShowReportInputs(true);
                        setReportPreview(previewItem);
                      }}
                    />
                  )
                ) : null}
                <Tooltip title={TOOLTIPS.viewRunInputs}>
                  <span>
                    <IconButton
                      disabled={downloadParametricAnalysisPdf === 'loading' || parametricAnalysisChartData.loading || hydraulics?.loading}
                      onClick={() => {
                        dispatch(listHydraulicsDetails(rowData));
                        setShowHydraulicsParameterDialog({
                          open: true,
                          data: rowData,
                          dialogParameter: 'parametricAnalysis'
                        });
                      }}>
                      <ShortcutOutlined fontSize='large' />
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton
                  onClick={() => {
                    handleChartAxisReset('resetAll');
                    onCloseClick();
                  }}
                >
                  <CancelPresentationRounded fontSize='large' />
                </IconButton>
              </Grid>
            </Grid>
          </Typography>
        </Box>
        <Divider />
        {rowData?.isParametricResults ? <>
          <Box display="flex" justifyContent="center" margin="1%" alignItems="center">
            {/* Filter Toggle Button */}
            {!parametricAnalysisChartData?.loading && (
              <Grid item>
                <Box
                  className={classes.runDetailsFilter}
                  onClick={() => setShowFilter(!showFilter)}
                >
                  {!showFilter && (<>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Filter</Typography>
                    <IconButton variant="solid" fontSize="large">
                      <FilterAltRounded />
                    </IconButton></>
                  )}
                </Box>
              </Grid>
            )}
            {/* Filter Table */}
            {showFilter && (
              <Grid item sx={{
                position: 'relative',
                flexBasis: `${filterTableWidth[columns_table.length - 2]}%`
              }}>
                {/* Datagrid inside a Box to allow absolute positioning */}
                <Box sx={{ position: 'relative' }}>
                  <Datagrid
                    fileName={`${wellDetails?.Well?.trim()}-${rowData?.name?.trim()}-Parametric-Analysis`}
                    toolbar={{
                      options: {
                        columns: false,
                        filters: false,
                        density: false,
                        export: {
                          enabled: true,
                        }
                      },
                    }}
                    sx={{
                      '.MuiDataGrid-cell': { fontSize: '1rem', padding: '2px' },
                      '.MuiDataGrid-columnHeader': { fontSize: '1rem', fontWeight: '900 !important' },
                      '& .MuiDataGrid-footerContainer': { display: 'none' },
                      '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                        outline: 'none !important',
                      },
                      '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus':
                      {
                        outline: 'none !important',
                      },
                    }}
                    data={filterData}
                    loading={parametricAnalysisChartData.loading}
                    columns={columns_table}
                    height={filterData?.length <= 4 ? '225px' : '285px'}
                    autoHeight={filterData?.length <= 4}
                    disableRowSelectionOnClick
                    hideFooterPagination
                    hideFooterSelectedRowCount
                    getRowHeight={() => 'auto'}
                    pageSize={100}
                  />

                  {/* Chevron Left Icon Positioned Over Datagrid on the Right */}
                  <IconButton
                    onClick={() => setShowFilter(false)}
                    className={classes.filterTogglerTableBtn}
                  >
                    <ChevronLeft sx={{ fontSize: '30px' }} />
                  </IconButton>
                </Box>
              </Grid>
            )}
          </Box>
          <Grid container item xs={showTable ? 8 : 12} marginTop={showFilter ? (filterData.length < 3 ? '-1.25%' : '1.5%') : '0%'} alignItems="center" justifyContent="center">
            <Grid item>
              <CustomLegend />
            </Grid>
          </Grid>
          <Box style={{
            overflow: 'auto',
          }}>
            <Grid container>
              <Grid item xs={showTable ? 7 : 12} sx={{
                p: 2,
                pt: 4,
                '.canvasjs-chart-container canvas': {
                  cursor: loader || hydraulics?.loading ? 'wait !important' : ''
                }
              }}>
                {parametricAnalysisChartData.loading
                  ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ height: '700px' }}
                    >
                      <CircularProgress />
                    </Box>
                    )
                  : (
                    <><CanvasJSChart
                      options={options}
                    />
                      <Box style={{ position: 'relative' }}>
                        <Box className={classes.xAxisEditBtn}>
                          <Box className={classes.editBox}>
                            <ChartsAxisPopover
                              title='X Axis'
                              defaultMin={defaultXAxis?.lowest - 0.1}
                              defaultMax={defaultXAxis?.highest + 0.1}
                              handleReset
                              onReset={() => handleChartAxisReset('x')}
                              onSubmit={(min, max) => {
                                dispatch({
                                  type: REDUX_ACTIONS.SET_HYDRAULICS_ECD_CHARTS_XAXIS,
                                  payload: { min: Number(min), max: Number(max) },
                                });
                              }} />
                          </Box>
                        </Box>

                        <Box className={classes.yAxisEditBtn}>
                          <Box className={classes.editBox}>
                            <ChartsAxisPopover
                              title='Y Axis'
                              defaultMin={defaultYAxis.min}
                              defaultMax={defaultYAxis.max}
                              onReset={() => handleChartAxisReset('y')}
                              onSubmit={(min, max) => {
                                dispatch({
                                  type: REDUX_ACTIONS.SET_HYDRAULICS_ECD_CHARTS_YAXIS,
                                  payload: { min: Number(min), max: Number(max) },
                                });
                              }} />
                          </Box>
                        </Box>
                      </Box>
                    </>

                    )}
              </Grid>
              {!showTable && <Box className={classes.menuTogglerBtn}>
                <IconButton
                  onClick={() => {
                    setShowTable(!showTable);
                  }}>
                  <ChevronLeft sx={{ fontSize: '33px' }} />
                </IconButton>
              </Box>}
              {showTable && <Grid item xs={5} marginTop={'1.5%'}>
                <Box className={classes.menuTogglerTableBtn}>
                  <IconButton
                    onClick={() => {
                      setShowTable(!showTable);
                    }}>
                    <ChevronRight sx={{ fontSize: '33px' }} />
                  </IconButton>
                </Box>
                <Box display={'flex'} justifyContent={'start'} marginLeft={'2.25%'}>
                  <Grid item xs={3}>
                    <FormControl fullWidth sx={{ bgcolor: 'transparent', width: '200px' }}>
                      <InputLabel id="filter-input-label">Type</InputLabel>
                      <Select
                        labelId="filter-select-id"
                        id="filter-id"
                        value={plotFor}
                        label="Filter"
                        disabled={parametricAnalysisChartData?.loading}
                        onChange={(e) => {
                          handleChartAxisReset('resetAll');
                          setDefaultXAxis({ min: '', max: '' });
                          setDefaultYAxis({ min: '', max: '' });
                          setPlotFor(e.target.value);
                        }}
                        sx={{
                          fontWeight: 'bolder',
                          fontSize: '18px',
                          background: 'unset'
                        }}
                      >
                        {['ESD', 'ECD'].map((item, idItem) => (
                          <MenuItem
                            key={`item_${item}_${idItem}`}
                            value={item}
                            sx={{ color: item?.color, fontSize: '18px', fontWeight: 'bold' }}
                          >
                            {item === 'ESD' ? 'ESD (Connection)' : 'ECD (Drilling)'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Box>
                {showTable && <Grid item xs={12}>
                  <Datagrid
                    fileName={`${wellDetails?.Well?.trim()}-${rowData?.name?.trim()}-Parametric-Analysis`}
                    loading={parametricAnalysisChartData.loading}
                    data={tableRow}
                    sx={{
                      '.MuiDataGrid-cell': {
                        fontSize: '1rem',
                        padding: '2px',
                      },
                      '.MuiDataGrid-columnHeader': {
                        fontSize: '1.2rem',
                        fontWeight: '900 !important',
                      },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        overflow: 'visible',
                        lineHeight: '1.43rem',
                        whiteSpace: 'normal',
                      },
                      '& .MuiDataGrid-footerContainer': {
                        minHeight: '0px'
                      },
                      '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                        outline: 'none !important',
                      },
                      '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus':
                      {
                        outline: 'none !important',
                      },
                    }}
                    columns={columns(plotFor, parametricAnalysisChartData?.units, renderRunCell, renderRunHideCell, customColumn)}
                    onCellClick={(row) => {
                      if (row.field === 'runNumber' && row?.row?.depth_points?.length) {
                        setShowRunChart({
                          open: true,
                          data: row
                        });
                      }
                    }}
                    disableRowSelectionOnClick
                    disableStickyHeader={true}
                    autoHeight={false}
                    {...(tableRow.length <= 100
                      ? { hideFooterPagination: true, hideFooterSelectedRowCount: true }
                      : {})}
                    height={'800px'}
                    getRowHeight={() => 'auto'}
                    pageSize={100}
                    toolbar={{
                      options: {
                        columns: false,
                        filters: false,
                        density: false,
                        export: {
                          enabled: true,
                        }
                      },
                    }}
                  />
                </Grid>}
              </Grid>}
            </Grid>
          </Box>
        </> : <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '50vh',
              textAlign: 'center',
              p: 4,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}
            >
              No Results Found
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.disabled' }}
            >
              Try adjusting parameters to generate results.
            </Typography>
          </Box>
        </>}
        </>)}
        {
          showReportInput
            ? <ReportInputsModal
              isOpen={showReportInput}
              tableList={tableList}
              setTableList={setTableList}
              onCloseClick={() => {
                setShowReportInputs(false);
              }}
              generateReport={handleDownloadPDF}
            />
            : null
        }
        {showHydraulicsParameterDialog.open && showHydraulicsParameterDialog?.data?.name
          ? (
            <HydraulicsDetailsModal
              selectedJob={wellDetails}
              isOpen={showHydraulicsParameterDialog.open}
              onCloseClick={() => {
                setShowHydraulicsParameterDialog({
                  open: false,
                  data: {}
                });
                dispatch({
                  type: REDUX_ACTIONS.HYDRAULICS_SET_ID,
                  payload: {
                    data: '',
                  },
                });
              }}
              data={showHydraulicsParameterDialog.data}
              dialogParameter={showHydraulicsParameterDialog.dialogParameter}
              wellDetails={hydraulicWellsDetails}
            />
            )
          : null}
          <VersionText version={parametricAnalysisChartData?.run_details_copy?.ph_version} loading={parametricAnalysisChartData.loading} />
      </Dialog>
      {
        showRunChart.open
          ? <RunChartDialog
            wellDetails={wellDetails}
            data={showRunChart.data}
            rowData={rowData}
            isOpen={showRunChart.open}
            onCloseClick={() => {
              setShowRunChart({
                open: false,
                data: null
              });
            }}
          />
          : null
      }
    </React.Fragment>
  );
};
ParametricAnalysisChart.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  rowData: PropTypes.any,
  wellDetails: PropTypes.any,
  hydraulicWellsDetails: PropTypes.any
};
ParametricAnalysisChart.defaultProps = {
  selectedJob: {}
};

export default ParametricAnalysisChart;
